import html2canvas from 'html2canvas';
import { getPlatform, loadJs } from '@king-fisher/mobileweb-lib';
import ShareComponent from '@/components/common/CommonShare.vue';
import { COMMON_SHARE_NAME, COMMON_SHARE_CDN } from '@/config/config';
import { statClick } from '@/config/stat';

const DynamicComponentId = 'dynamic-component-container';

/**
 * 动态创建组件
 */
const createDCInstance = (componentConfig, propsData = {}) => {
  // 动态组件引用，需要根据项目自定义
  const dynamicComponent = ShareComponent;
  const container = document.createElement('div');
  container.id = DynamicComponentId;

  // 设置容器样式：不在视图中可见，但保持渲染
  Object.assign(container.style, {
    position: 'fixed',
    top: '-9999px',
    left: '-9999px',
    zIndex: '-1',
    opacity: '0',
    pointerEvents: 'none',
    visibility: 'visible',
    overflow: 'hidden',
  });

  document.body.appendChild(container);

  const instance = new window.Vue({
    render: h =>
      h(
        dynamicComponent,
        Object.assign(
          {
            props: propsData,
          },
          componentConfig
        )
      ),
  });

  instance.$mount();
  container.appendChild(instance.$el);

  return {
    instance,
    container,
    destroy() {
      instance.$destroy();
      if (container.parentNode) {
        container.parentNode.removeChild(container);
      }
    },
  };
};

/**
 * 生成分享组件图片
 */
const generateShareImage = payload =>
  new Promise(resolve => {
    const dcInstance = createDCInstance({}, payload);
    const dom = dcInstance.instance.$el as HTMLElement;

    html2canvas(dom, {
      width: getPlatform() === 'iphone' ? 750 : 375,
      removeContainer: true,
      useCORS: true,
      foreignObjectRendering: false,
      ignoreElements: el =>
        // 如果el的父元素是body，且el元素的id不是dynamic-component-container，则忽略
        el.parentElement === document.body && el.id !== DynamicComponentId,
      scale: 2,
    })
      .then(canvas => {
        const base64 = canvas.toDataURL();
        resolve(base64);
      })
      .finally(() => {
        dcInstance.destroy();
      });
  });

let isSharing = false;
let isCDNLoaded = false; // 标记CDN是否已加载
let shareComponentInstance = null; // 缓存分享组件实例
let shareComponentReadyPromise = null; // 初始化过程复用

const initShareComponent = () => {
  if (shareComponentInstance) {
    return Promise.resolve(shareComponentInstance);
  }

  if (shareComponentReadyPromise) {
    return shareComponentReadyPromise;
  }

  shareComponentReadyPromise = new Promise(resolve => {
    let container = document.getElementById('common-share-container');
    if (!container) {
      container = document.createElement('div');
      container.id = 'common-share-container';
      document.body.appendChild(container);
    }

    const { Vue } = window;
    const CommonShareComp = window[COMMON_SHARE_NAME].default;
    const shareInstance = new Vue({
      render: h =>
        h(CommonShareComp, {
          props: { falconShareType: 'image', needShareBtn: false, shareBmpRes: 1 },
          ref: 'shareComponent',
        }),
    }).$mount(container);

    shareComponentInstance = shareInstance.$refs.shareComponent;
    resolve(shareComponentInstance);
  }).finally(() => {
    shareComponentReadyPromise = null;
  });

  return shareComponentReadyPromise;
};

export function shareReport(payload) {
  if (isSharing) {
    return;
  }

  isSharing = true;

  const share = async () => {
    statClick('ths_mob_yanbao_pagetop_jdindexshare');
    const component = await initShareComponent();
    const img = await generateShareImage(payload);
    component.openSharePop({ bmpUrl: img });
    component.shareBmpUrl = img;
  };

  const ensureCDN = () => {
    if (isCDNLoaded && window[COMMON_SHARE_NAME]) {
      return Promise.resolve();
    }

    return loadJs(COMMON_SHARE_CDN, 'utf-8').then(() => {
      isCDNLoaded = true; // 标记CDN已加载
    });
  };

  ensureCDN()
    .then(share)
    .finally(() => {
      isSharing = false;
    });
}
